<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm']);

const DEFAULT_COLOR = '#1f93ff';

const { t } = useI18n();
const dialogRef = ref(null);
const isEditMode = ref(false);

const form = reactive({ id: null, name: '', color: DEFAULT_COLOR });

const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.id = null;
  form.name = '';
  form.color = DEFAULT_COLOR;
};

const open = (stage = null) => {
  isEditMode.value = !!stage;
  form.id = stage?.id || null;
  form.name = stage?.name || '';
  form.color = stage?.color || DEFAULT_COLOR;
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;
  emit('confirm', { id: form.id, name: form.name.trim(), color: form.color });
};

const closeDialog = () => dialogRef.value?.close();

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditMode ? t('KANBAN.STAGES.EDIT.TITLE') : t('KANBAN.STAGES.ADD.TITLE')
    "
    width="md"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('KANBAN.STAGES.ADD.NAME_LABEL')"
        :placeholder="t('KANBAN.STAGES.ADD.NAME_PLACEHOLDER')"
        :disabled="isLoading"
        autofocus
      />
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.STAGES.ADD.COLOR_LABEL') }}
        </label>
        <ColorPicker v-model="form.color" />
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="
            isEditMode
              ? t('KANBAN.STAGES.EDIT.SUBMIT')
              : t('KANBAN.STAGES.ADD.SUBMIT')
          "
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
